import React, { useContext, useRef, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form, { RuleObject } from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { SurveyInput } from "pages/symptoms/surveyForms/surveyInput";
import wongBakerFaces from "./wongBakerFaces.png";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface AchesAndPainFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const AchesAndPainForm: React.FC<AchesAndPainFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);
  const [otherTempInput, setOtherTempInput] = useState("");
  const otherInputRef = useRef("");

  const totalPages = 2;
  const progressPercent = Math.round((currentPage / totalPages) * 100);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingAchesAndPainMutation,
    mutate: submitAchesAndPainForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addAchesAndPainsFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        pain: localStorage.getItem("hasAchesOrPains") || "",
        length: localStorage.getItem("durationOfPain") || "",
        frequency: localStorage.getItem("frequencyOfPain") || "",
        level: localStorage.getItem("levelOfPain") || "",
        location: localStorage.getItem("whereIsYourPain") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasAchesOrPains") === "No") {
      form.resetFields([
        "Level of pain",
        "Where is your pain",
        "Frequency of pain",
        "Duration of pain"
      ]);
      localStorage.setItem("levelOfPain", "");
      localStorage.setItem("whereIsYourPain", "");
      localStorage.setItem("frequencyOfPain", "");
      localStorage.setItem("durationOfPain", "");
      submitAchesAndPainForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitAchesAndPainForm();
    }
  };

  const validateWhereIsYourPain = (
    rules: RuleObject,
    value: string,
    callback: (error?: string) => void
  ) => {
    if (
      otherInputRef.current === "" &&
      localStorage.getItem("whereIsYourPain")?.includes("Other")
    ) {
      callback("Please enter value for 'Other' field.");
    } else {
      callback();
    }
  };

  const handleFinishFailed = (errorInfo: any) => {
    const whereIsYourPainErrorIndex = errorInfo.errorFields.findIndex(
      (field: any) => field.name[0] === "Where is your pain"
    );

    if (whereIsYourPainErrorIndex !== -1) {
      const errorFieldNode = document.getElementById("Where is your pain");
      errorFieldNode?.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Aches or pains"]);
    } else {
      form.resetFields([
        "Level of pain",
        "Where is your pain",
        "Frequency of pain",
        "Duration of pain"
      ]);
    }
  };

  return (
    <SurveyPage platform={platform}>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="achesAndPains"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          onFinishFailed={handleFinishFailed}
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          <SurveyProgressBar
            percent={progressPercent}
            currentStep={currentPage}
            totalSteps={totalPages}
          />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you had aches or pains, other than chest pain, in the last month?"
                required
              >
                <FormItemStyle
                  name="Aches or pains"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("hasAchesOrPains")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem("hasAchesOrPains", e.target.value);
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard title="Indicate level of pain" required>
                <FormItemStyle
                  name="Level of pain"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("levelOfPain")}
                    onChange={e =>
                      localStorage.setItem("levelOfPain", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid columns={5}>
                      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(
                        val => (
                          <AnswerOption key={val}>
                            <Radio value={val}>{val}</Radio>
                          </AnswerOption>
                        )
                      )}
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard style={{ textAlign: "center" }}>
                <img
                  src={wongBakerFaces}
                  alt="Wong Baker Faces Scale"
                  style={{
                    maxHeight: platform === Platform.MOBILE ? "120px" : "225px",
                    maxWidth: "100%",
                    objectFit: "contain"
                  }}
                />
              </SurveyQuestionCard>

              <SurveyQuestionCard title="Where is your pain?" required>
                <FormItemStyle
                  name="Where is your pain"
                  label=""
                  rules={[
                    { required: true },
                    {
                      validator: validateWhereIsYourPain,
                      validateTrigger: "submit"
                    }
                  ]}
                  initialValue={""}
                >
                  <Checkbox.Group
                    style={{ width: "100%" }}
                    onChange={e =>
                      localStorage.setItem("whereIsYourPain", e.toString())
                    }
                    value={sessionStorage
                      .getItem("whereIsYourPain")
                      ?.split(/,/)}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Checkbox value="Joints">Joints</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Back">Back</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Head">Head</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Abdomen">Abdomen</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Legs/Feet">Legs/Feet</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Other">Other</Checkbox>
                        <SurveyInput
                          value={otherTempInput || otherInputRef.current}
                          placeholder="Please specify"
                          onChange={e => {
                            setOtherTempInput(e.target.value);
                            otherInputRef.current = e.target.value;
                          }}
                          style={{ marginTop: "8px", width: "100%" }}
                        />
                      </AnswerOption>
                    </AnswerList>
                  </Checkbox.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="How often have you had the pain in the last one month?"
                required
              >
                <FormItemStyle
                  name="Frequency of pain"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("frequencyOfPain")}
                    onChange={e =>
                      localStorage.setItem("frequencyOfPain", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Rarely (1 - 3 times)">
                          Rarely (1 - 3 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Occasionally (4 - 7 times)">
                          Occasionally (4 - 7 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Frequently (8 - 15 times)">
                          Frequently (8 - 15 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Nearly everyday">Nearly everyday</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="How long did the pain last? (List longest episode)"
                required
              >
                <FormItemStyle
                  name="Duration of pain"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("durationOfPain")}
                    onChange={e =>
                      localStorage.setItem("durationOfPain", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Less than 5 minutes">
                          Less than 5 minutes
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="5 - 30 minutes">5 - 30 minutes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Several hours">Several hours</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Several days">Several days</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingAchesAndPainMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
